import { useEffect, useMemo } from 'react'
import { gql, ApolloError } from '@apollo/client'
import { useQuery } from '@sketch/graphql-apollo'
import {
  ShareListItemFragment,
  ShareListItemFragmentDoc,
} from '@sketch/gql-types'
import { useRemoveRecentlyOpenedShares } from '../hooks/useRecentlyOpenedSharesStore'

/**
 * TEMPORARY: fetch many shares in a single request using GraphQL aliases.
 *
 * The GraphQL schema has no `shares(ids: [UUID!]!)` query, so to render the
 * "Recently Opened" list we'd otherwise have to fire one `getShare` per id
 * (N HTTP round-trips, since our Apollo client uses `createHttpLink`, not
 * `BatchHttpLink`).
 *
 * Instead, we build ONE query at runtime that asks for the `share` field once
 * per id. Asking for the same field multiple times in a single query is only
 * legal if each occurrence is given a unique response key — a GraphQL
 * "alias" (a standard part of the GraphQL spec, see
 * https://spec.graphql.org/October2021/#sec-Field-Alias — NOT an Apollo
 * feature). An alias is the `label:` prefix in front of a field:
 *
 *   query ($id_0: UUID!, $id_1: UUID!) {
 *     share_0: share(id: $id_0) { ...ShareListItem }   # alias = "share_0"
 *     share_1: share(id: $id_1) { ...ShareListItem }   # alias = "share_1"
 *   }
 *
 * The server then returns `{ share_0: {...}, share_1: {...} }`, i.e. the
 * response is keyed by our aliases instead of all collapsing onto a single
 * "share" key. This collapses N requests into a single HTTP round-trip,
 * single Apollo cache write, and single loading/error state — without
 * touching the link chain. Ids are reused from the existing `ShareListItem`
 * fragment so each share's selection stays type-safe.
 *
 * Remove this hook and replace with a static `.gql` query the moment the
 * backend ships a real `shares(ids: [UUID!]!)` resolver.
 */

// Prefix for the per-share response alias (`share_0`, `share_1`, …) and the
// matching query variable (`id_0`, `id_1`, …). The alias prefix is arbitrary;
// it only has to be a valid GraphQL name that's unique per field.
const ALIAS_PREFIX = 'share_'
const VAR_PREFIX = 'id_'

// The query response: an object keyed by the aliases above
// (`{ share_0: Share | null, share_1: Share | null, … }`), one entry per id.
// A value is `null` when that share was deleted or is no longer accessible.
type RecentSharesQueryResponse = Record<string, ShareListItemFragment | null>
type RecentSharesQueryVars = Record<string, string>

/**
 * Build a GraphQL document that requests the `share` field once per id, each
 * occurrence given a unique alias (`share_0:`, `share_1:`, …) so the multiple
 * `share` selections don't collide. Every id becomes a `UUID!` variable; ids
 * are passed as query variables, never interpolated into the query string.
 */
function buildRecentSharesDocument(idCount: number) {
  if (idCount === 0) {
    // Apollo requires a valid document even when skipped; return a placeholder.
    return gql`
      query GetRecentlyOpenedSharesEmpty {
        __typename
      }
    `
  }

  const indices = Array.from({ length: idCount }, (_, i) => i)
  const varDecls = indices.map(i => `$${VAR_PREFIX}${i}: UUID!`).join(', ')
  // Each entry is an aliased field: `share_0: share(id: $id_0) { … }`.
  // The `share_N:` part is the alias — it becomes the key in the response.
  const fields = indices
    .map(
      i =>
        `${ALIAS_PREFIX}${i}: share(id: $${VAR_PREFIX}${i}) { ...ShareListItem }`
    )
    .join('\n    ')

  return gql`
    query GetRecentlyOpenedShares(${varDecls}) {
      ${fields}
    }
    ${ShareListItemFragmentDoc}
  `
}

function buildVariables(ids: string[]): RecentSharesQueryVars {
  const vars: RecentSharesQueryVars = {}
  ids.forEach((id, i) => {
    vars[`${VAR_PREFIX}${i}`] = id
  })
  return vars
}

interface UseRecentlyOpenedSharesQueryResult {
  shares: ShareListItemFragment[]
  loading: boolean
  error?: ApolloError
}

/**
 * Fetches metadata for all `ids` in a single network request via the aliased
 * multi-field workaround documented above. Returns shares in the same order
 * as `ids`; null responses (deleted or no-longer-accessible shares) are
 * filtered out and pruned from the recently-opened localStorage list.
 */
export function useRecentlyOpenedSharesQuery(
  ids: string[],
  workspaceId: string
): UseRecentlyOpenedSharesQueryResult {
  // Memoize on the joined ids so the document identity is stable across
  // re-renders that don't actually change the id list.
  const idsKey = ids.join(',')

  const document = useMemo(
    () => buildRecentSharesDocument(ids.length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ids.length]
  )

  const variables = useMemo(
    () => buildVariables(ids),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idsKey]
  )

  const { data, loading, error } = useQuery<
    RecentSharesQueryResponse,
    RecentSharesQueryVars
  >(document, {
    variables,
    skip: ids.length === 0,
  })

  const removeRecentlyOpened = useRemoveRecentlyOpenedShares()

  // Walk the requested ids and read each one's aliased field off the response
  // (`ids[i]` ↔ `data.share_${i}`). The server returns a share when it could
  // be resolved, or `null` when it couldn't — i.e. the share was deleted or the
  // user no longer has access. We split the result into two lists:
  //   - `shares`: the resolved shares, in the same order as `ids`
  //   - `missingIds`: the ids whose share came back `null`, used below to
  //     prune them from the recently-opened localStorage list
  const { shares, missingIds } = useMemo(() => {
    if (!data || ids.length === 0) {
      return {
        shares: [] as ShareListItemFragment[],
        missingIds: [] as string[],
      }
    }
    const shares: ShareListItemFragment[] = []
    const missing: string[] = []
    ids.forEach((id, i) => {
      const share = data[`${ALIAS_PREFIX}${i}`]
      if (share) {
        shares.push(share)
      } else {
        missing.push(id)
      }
    })
    return { shares, missingIds: missing }
  }, [data, ids])

  // Prune deleted/inaccessible shares from localStorage so the list self-heals
  // over time without needing a separate revalidation pass.
  useEffect(() => {
    if (missingIds.length > 0 && workspaceId) {
      removeRecentlyOpened(missingIds, workspaceId)
    }
  }, [missingIds, workspaceId, removeRecentlyOpened])

  return { shares, loading, error }
}
